import NProgress from 'nprogress';
import swal from 'sweetalert';
import axios, { Method } from 'axios';

import AuthService from './AuthService';
import { API_URL, CLIENT_ID as client_id, CLIENT_SECRET as client_secret } from './constants';

axios.defaults.baseURL = API_URL;
NProgress.configure({ trickleSpeed: 150 });

const transformUrl = (path, data) =>
  API_URL +
  '/' +
  path +
  `?${Object.keys(data)
    .map((key) => key + '=' + data[key])
    .join('&')}`;

function apiRequest<T>(method: Method, url: string, data: object = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    const headers = AuthService.loggedIn()
      ? {
          'x-nc-type': 'b',
          'x-nc-version': '2.8',
          Authorization: AuthService.getToken(),
        }
      : {};
    if (method === 'get') {
      url += `?${Object.keys(data)
        .map((key) => key + '=' + data[key])
        .join('&')}`;
    }

    NProgress.start();

    axios({ method, url, data, headers })
      .then((res) => {
        NProgress.done();
        resolve(res.data as T);
      })
      .catch((error: any) => {
        NProgress.done();
        reject(error);
        swal('An error occured!', error.response?.data?.error?.message, 'error');
      });
  });
}

const billing = {
  createCustomer: (email: string) => apiRequest('post', '/payments/customer', { email }),
  updateEmail: (email: string) => apiRequest('post', '/payments/email', { email }),
  sendStripeToken: (source: string) => apiRequest('post', '/payments/sources', { source }),
  removeSource: (source: string) => apiRequest('delete', '/payments/sources', { source }),
  setDefaultSource: (source: string) => apiRequest('post', '/payments/sources/default', { source }),
  getPaymentMethods: () => apiRequest('get', '/payments/customer'),
  sendPlaidToken: (token: string, account_id: string) =>
    apiRequest('post', '/payments/sources/plaid', { token, account_id }),
  submitPlan: (plan: string, frequency: string) =>
    apiRequest('post', '/payments/subscriptions/start', { plan, frequency }),
};

interface loginData {
  username: string;
  password: string;
}

const auth = {
  login: (form: loginData) =>
    apiRequest('post', '/oauth/token', {
      client_id,
      client_secret,
      grant_type: 'password',
      ...form,
    }),
  oneTimeToken: (token: string) => apiRequest('post', '/report/replaceToken', { token }),
  forgotPassword: (form: any) => apiRequest('post', '/users/password/forgot', form),
  forgotUsername: (form: any) =>
    apiRequest('post', '/users/password/username', { type: 'business', ...form }),
};

const report = {
  monthList: () => apiRequest('get', '/report/months'),
  monthly: (date: MonthlyReportDate) => apiRequest('get', '/report/monthly', date),
  static: (id: string) => apiRequest<{ data: Report }>('get', `/report/${id}`),
  signContentDownload: (month: any, year: any) =>
    apiRequest('get', 'report/zipSign', { month, year, version: 'v1.3' }),
  downloadZip: (id: any) => transformUrl('report/zip', { id }),
};

const business = {
  info: () => apiRequest('get', '/users/me'),
  updateInfo: (data: any) => apiRequest('patch', '/users/me', data),
  getHours: (businessId: string) => apiRequest('get', `/businesses/${businessId}/working-hours`),
  updateHours: (hours: any, businessId: string) =>
    apiRequest('put', `/businesses/${businessId}/working-hours`, hours),
};

// enum is reserved by JS
const enm = {
  getCities: () => apiRequest('get', '/enum/cities'),
  getCarriers: () => apiRequest('get', '/enum/carriers'),
  getShoeSizes: () => apiRequest('get', '/enum/model_shoe_sizes', { filter: 'all' }),
  getSuitSizes: () => apiRequest('get', '/enum/model_suit_sizes'),
  getWaistSizes: () => apiRequest('get', '/enum/model_waist_sizes'),
  getDressSizes: () => apiRequest('get', 'enum/model_dress_sizes'),
  getStatOptions: () => apiRequest('get', '/enum/model_stats'),
  getModelClassifications: () => apiRequest('get', '/enum/model_classifications', { type: '3' }),
  getCreatorClassifications: () => apiRequest('get', '/enum/model_classifications', { type: '2' }),
};

const offer = {
  getOldOffers: (business: string): Promise<any> =>
    apiRequest<{ data: Offer[] }>('get', `/businesses/${business}/deals/previous`),
  getOffers: (business: string, type: string, data: any) =>
    apiRequest('get', `/businesses/${business}/deals/${type}`, data),
  getApplicants: (id: string, data: Object) => apiRequest('get', `/deals/${id}/applicants`, data),
  getById: (id: string, data: Object = {}) => apiRequest('get', `/deals/${id}`, data),
  getPeriods: (business: string) =>
    apiRequest<{ data: OfferPeriod[] }>('get', `/businesses/${business}/deals/past/periods`),
  cancelOffer: (deal: string) => apiRequest('delete', `/deals/${deal}`),
  cancelUpcomingOffer: (templateId: string, templateKey: string) =>
    apiRequest('delete', `/deals/${templateKey}`),
  cancelProjectedOffer: (templateId: string, key: string) =>
    apiRequest('delete', `/templates/${templateId}/${key}`),
  checkIn: (id: string) => apiRequest('post', `/claims/${id}/check_in/`),
  setCallSheetInformation: (id: string, data: Object, attendees: Object) =>
    apiRequest('post', `/deals/${id}/call_sheet/`, { ...data, attendees }),
  setTrackingNumber: (id: string, trackingNumber: string, carrier?: string) =>
    apiRequest('post', `/claims/${id}/shipment/`, {
      carrier: carrier,
      tracking_number: trackingNumber,
    }),
  sendPushToAddTracking: (id: string) => apiRequest('post', `/claims/${id}/shipment/push`),
  createOffer: (businessId: string, data: any) =>
    apiRequest('post', `/businesses/${businessId}/deals/`, data),
  cancelGroup: (group) => apiRequest('delete', `/deals/groups/${group}`),
  cancelClaim: (dealId, claimId) =>
    apiRequest('delete', `/deals/${dealId}/attendees/`, { attendees: [claimId] }),
  setApplicationStatus: (attendeeId: string, status: ApplyOfferApplicantStatus) =>
    apiRequest('put', `/claims/${attendeeId}/application`, { status: status }),
  finalizeOffer: (deal: Offer) => apiRequest('post', `deals/${deal.id}/applicants`, deal),
  reserveForClaim: (id: string, reserve_for: string) =>
    apiRequest('post', `claims/${id}/reserve`, { reserve_for }),
};

const user = {
  getProperty: (name: string, d: any) =>
    apiRequest('get', `/users/me/properties`, {
      name: name,
      default: d,
    }),
  setProperty: (name: string, value: string, expires_at: string) =>
    apiRequest('post', `/users/me/properties`, {
      name: name,
      value: value,
      expires_at: expires_at,
    }),
};

const instagram = {
  getPeriods: (id) => apiRequest('get', `/businesses/${id}/ig-actions/periods`),
  getPeriodResults: (period: string, id: string) =>
    apiRequest('get', `/businesses/${id}/ig-actions/periods/${period}`),
  getMedia: (user_social: string) => apiRequest('get', `social/${user_social}/media`),
};

const media = {
  get: (data: { last_id?: string } = {}) => apiRequest('get', '/users/me/media', data),
  add: (data: { source: string; urls?: Array<string>; images?: any }) =>
    apiRequest('post', '/users/me/media', data),
  sort: (data) => apiRequest('post', '/users/me/media/sort', data),
  remove: (data) => apiRequest('delete', '/users/me/media', data),
};

export { billing, auth, report, business, offer, instagram, media, user, enm };
